/**
 * Registration screen (Req 22.1, 22.2, 22.3).
 *
 * Collects an email and a password and submits them to the Identity_Service to
 * create an account. It:
 *   - renders labelled email + password fields, all text resolved through the
 *     i18n catalog via `useT` (Req 22.11);
 *   - validates client-side on submit and BLOCKS submission while flagging EACH
 *     invalid field with a field-level User_Message (Req 22.2), using the pure
 *     {@link validateRegistration} helper so the rule is testable in isolation
 *     (task 15.2);
 *   - on a valid submit, invokes the injected {@link RegistrationScreenProps.onRegister}
 *     — the Identity_Service call — and on success shows the verify-email
 *     User_Message (Req 22.3).
 *
 * Boundary: the Identity_Service call is an INJECTED prop, not a direct backend
 * import. `web` may import shared TYPES from `@avos/foundation` (e.g.
 * {@link ErrorEnvelope}) but never backend runtime — the enclosing app wires the
 * real call in. This keeps the screen testable without a backend and respects
 * the web module boundary.
 */
import { useState, type FormEvent, type ReactElement } from "react";
import type { ErrorEnvelope } from "@avos/foundation";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";
import {
  validateRegistration,
  isRegistrationValid,
  type RegistrationFieldErrors,
} from "./validateRegistration.js";

/**
 * The outcome of the injected Identity_Service registration call. Success shows
 * the verify-email message (Req 22.3); failure carries a backend
 * {@link ErrorEnvelope} the screen renders as a form-level User_Message.
 */
export type RegistrationResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: ErrorEnvelope };

/** Props for {@link RegistrationScreen}. */
export interface RegistrationScreenProps {
  /**
   * The Identity_Service registration call, injected so the screen never
   * imports backend runtime (web boundary). Called only after client-side
   * validation passes.
   */
  readonly onRegister?: (input: {
    email: string;
    password: string;
  }) => Promise<RegistrationResult>;
}

/** The submit lifecycle state, used to drive the success/failure messages. */
type SubmitState =
  | { readonly kind: "idle" }
  | { readonly kind: "submitting" }
  | { readonly kind: "success" }
  | { readonly kind: "error"; readonly error: ErrorEnvelope };

export default function RegistrationScreen({
  onRegister,
}: RegistrationScreenProps = {}): ReactElement {
  const t = useT();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<RegistrationFieldErrors>({
    email: null,
    password: null,
  });
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    // Validate every field; flag EACH invalid one and BLOCK submission if any
    // field is invalid (Req 22.2).
    const errors = validateRegistration(email, password);
    setFieldErrors(errors);
    if (!isRegistrationValid(errors)) {
      setSubmit({ kind: "idle" });
      return;
    }

    // Valid: call the injected Identity_Service (Req 22.1).
    setSubmit({ kind: "submitting" });
    if (onRegister === undefined) {
      // No service wired (e.g. isolated render): treat as success so the
      // verify-email message is shown; the real call is injected by the host.
      setSubmit({ kind: "success" });
      return;
    }
    const result = await onRegister({ email, password });
    setSubmit(
      result.ok
        ? { kind: "success" }
        : { kind: "error", error: result.error },
    );
  }

  // On success, replace the form with the verify-email User_Message (Req 22.3).
  if (submit.kind === "success") {
    const verifyMessage: UserMessageModel = {
      what: t("auth.register.verifyEmailSent.what"),
      why: t("auth.register.verifyEmailSent.why"),
      next: t("auth.register.verifyEmailSent.next"),
    };
    return (
      <section aria-labelledby="screen-heading" data-screen="register">
        <h1 id="screen-heading">{t("auth.register.title")}</h1>
        <UserMessage message={verifyMessage} severity="status" />
      </section>
    );
  }

  const emailErrorId = "register-email-error";
  const passwordErrorId = "register-password-error";

  return (
    <section aria-labelledby="screen-heading" data-screen="register">
      <h1 id="screen-heading">{t("auth.register.title")}</h1>
      <p>{t("auth.register.prompt")}</p>

      {/* Form-level failure from the Identity_Service (Req 22.1). */}
      {submit.kind === "error" ? (
        <UserMessage
          message={{
            what: submit.error.error.message,
            why: submit.error.error.detail,
            ...(submit.error.error.next !== undefined &&
            submit.error.error.next.trim() !== ""
              ? { next: submit.error.error.next }
              : {}),
            code: submit.error.error.code,
            requestId: submit.error.error.request_id,
          }}
          severity="error"
        />
      ) : null}

      <form onSubmit={handleSubmit} noValidate data-testid="register-form">
        <div className="field">
          <label htmlFor="register-email">
            {t("auth.register.emailLabel")}
          </label>
          <input
            id="register-email"
            name="email"
            type="email"
            value={email}
            placeholder={t("auth.register.emailPlaceholder")}
            aria-invalid={fieldErrors.email !== null}
            aria-describedby={
              fieldErrors.email !== null ? emailErrorId : undefined
            }
            onChange={(e) => setEmail(e.target.value)}
          />
          {/* Field-level User_Message flagging an invalid email (Req 22.2). */}
          {fieldErrors.email !== null ? (
            <div id={emailErrorId}>
              <UserMessage
                message={{
                  what: t(fieldErrors.email),
                  why: t("auth.register.prompt"),
                }}
                severity="error"
              />
            </div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="register-password">
            {t("auth.register.passwordLabel")}
          </label>
          <input
            id="register-password"
            name="password"
            type="password"
            value={password}
            placeholder={t("auth.register.passwordPlaceholder")}
            aria-invalid={fieldErrors.password !== null}
            aria-describedby={
              fieldErrors.password !== null ? passwordErrorId : undefined
            }
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* Field-level User_Message flagging an empty password (Req 22.2). */}
          {fieldErrors.password !== null ? (
            <div id={passwordErrorId}>
              <UserMessage
                message={{
                  what: t(fieldErrors.password),
                  why: t("auth.register.prompt"),
                }}
                severity="error"
              />
            </div>
          ) : null}
        </div>

        <button type="submit" disabled={submit.kind === "submitting"}>
          {t("auth.register.submit")}
        </button>
      </form>
    </section>
  );
}
